import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import '../styles/_quizResults.scss';

const NUM_QUESTIONS = 10;

export default function QuizResults() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const getApiBase = () =>
    process.env.NODE_ENV === 'production'
      ? 'https://alpine-larochelle.com'
      : (window.location.port === '3000' ? 'http://localhost:5000' : window.location.origin);

  useEffect(() => {
    const authStatus = localStorage.getItem('isAdminAuthenticated');
    setIsAuthenticated(authStatus === 'true');
    if (authStatus !== 'true') {
      navigate('/SecretAdmin', { replace: true });
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const apiBase = getApiBase();
    fetch(`${apiBase}/api/quiz-results`)
      .then((res) => {
        if (!res.ok) throw new Error('Erreur lors du chargement des résultats.');
        return res.json();
      })
      .then((data) => {
        setResults(Array.isArray(data) ? data : []);
        setError('');
      })
      .catch((err) => {
        setError(err.message || 'Erreur réseau');
        setResults([]);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const getQuestionLabel = (result, questionIndex) => {
    if (!result.answers || !Array.isArray(result.answers)) return '—';
    const a = result.answers.find((x) => x.questionIndex === questionIndex);
    if (a == null) return '—';
    return a.correct ? 'Vrai' : 'Faux';
  };

  const handleDelete = (result) => {
    const message = `Supprimer le résultat de ${result.prenom} ${result.nom} ?`;
    if (!window.confirm(message)) return;
    const apiBase = getApiBase();
    fetch(`${apiBase}/api/quiz-results/${result._id}`, { method: 'DELETE' })
      .then((res) => {
        if (!res.ok) throw new Error('Erreur lors de la suppression.');
        setResults((prev) => prev.filter((r) => r._id !== result._id));
      })
      .catch((err) => {
        setError(err.message || 'Impossible de supprimer ce résultat.');
      });
  };

  const downloadExcel = () => {
    const headers = [
      'Nom',
      'Prénom',
      ...Array.from({ length: NUM_QUESTIONS }, (_, i) => `Question ${i + 1}`),
      'Score',
      'Total',
      'Pourcentage (%)',
      'Date',
    ];

    const rows = results.map((r) => [
      r.nom || '',
      r.prenom || '',
      ...Array.from({ length: NUM_QUESTIONS }, (_, i) => getQuestionLabel(r, i)),
      String(r.score ?? ''),
      String(r.totalQuestions ?? ''),
      String(r.percentage ?? ''),
      r.createdAt ? new Date(r.createdAt).toLocaleString('fr-FR') : '',
    ]);

    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const colWidths = [
      { wch: 18 },
      { wch: 18 },
      ...Array(NUM_QUESTIONS).fill({ wch: 10 }),
      { wch: 8 },
      { wch: 8 },
      { wch: 14 },
      { wch: 20 },
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Résultats quiz');

    XLSX.writeFile(wb, 'resultats_quiz_alpine.xlsx');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="quiz-results-page">
      <div className="quiz-results-container">
        <div className="quiz-results-header">
          <h1>Résultats du quiz</h1>
          <div className="quiz-results-actions">
            <Link to="/SecretAdmin" className="button quiz-results-back">
              Retour à l'admin
            </Link>
            <button type="button" className="button quiz-results-download" onClick={downloadExcel}>
              Télécharger en Excel (.xlsx)
            </button>
          </div>
        </div>

        {loading && <p className="quiz-results-loading">Chargement des résultats...</p>}
        {error && <p className="quiz-results-error">{error}</p>}

        {!loading && !error && (
          <div className="quiz-results-table-wrap">
            <table className="quiz-results-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Prénom</th>
                  {Array.from({ length: NUM_QUESTIONS }, (_, i) => (
                    <th key={i}>Q{i + 1}</th>
                  ))}
                  <th>Score</th>
                  <th>%</th>
                  <th className="quiz-results-th-delete" aria-label="Supprimer" />
                </tr>
              </thead>
              <tbody>
                {results.length === 0 ? (
                  <tr>
                    <td colSpan={NUM_QUESTIONS + 5}>Aucun résultat pour le moment.</td>
                  </tr>
                ) : (
                  results.map((r, idx) => (
                    <tr key={r._id || idx}>
                      <td data-label="Nom">{r.nom}</td>
                      <td data-label="Prénom">{r.prenom}</td>
                      {Array.from({ length: NUM_QUESTIONS }, (_, i) => (
                        <td key={i} data-label={`Q${i + 1}`} className={getQuestionLabel(r, i) === 'Vrai' ? 'correct' : getQuestionLabel(r, i) === 'Faux' ? 'incorrect' : ''}>
                          {getQuestionLabel(r, i)}
                        </td>
                      ))}
                      <td data-label="Score">{r.score ?? '—'}</td>
                      <td data-label="%">{r.percentage != null ? `${r.percentage} %` : '—'}</td>
                      <td className="quiz-results-td-delete" data-label="">
                        <button
                          type="button"
                          className="quiz-results-delete-btn"
                          onClick={() => handleDelete(r)}
                          title="Supprimer cette entrée"
                          aria-label={`Supprimer le résultat de ${r.prenom} ${r.nom}`}
                        >
                          <svg className="quiz-results-delete-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
